import { MdAddCircleOutline } from "react-icons/md";
import { useContext, useEffect, useState } from "react";
import AssignLessonCard from "./AssignLessonCard";
import lessonInformation from "components/main-view/utils/functions/lessonInformation";
import {
  getCollectionRequest,
  updateRecordRequest,
} from "services/apiRequests";
import { Program, Course, Project, Module } from "utils/interfaces";
import SuccessPopUpMessage from "./SuccessPopUpMessage";
import EmptyViewPopUp from "components/main-view/utils/pop-up-panels/common/EmptyViewPopUp";
import { LoadingDots } from "utils/loadingAnimation";
import { motion } from "framer-motion";
import { logoutFunction } from "services/authRequests";
import { LessonsContext } from "components/utils/Contexts";
import BackButtonHeaderPopUp from "../common/BackButtonHeader";

interface AssignLessonPanelProps {
  selectedDataId: string;
  lessonListToShow: string;
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateLesson: (
    lesson: "programs" | "courses" | "projects" | "modules"
  ) => void;
}

/**
 * Definition of the variants of this component
 */
const variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeOut" },
  },
};

const AssignLessonPanel: React.FC<AssignLessonPanelProps> = ({
  selectedDataId,
  lessonListToShow,
  setShowPopUp,
  onCreateLesson,
}) => {
  // States definition
  const [lessonData, setLessonData] = useState<
    Program[] | Course[] | Project[] | Module[]
  >([]);
  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
  const [successPopUpText, setSuccessPopUpText] = useState("");
  const [addOrRemovePopUp, setAddOrRemovePopUp] = useState<"add" | "remove">(
    "add"
  );
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Get the context parameters
  const { setLinkedId, setLinkedLesson } = useContext(LessonsContext);

  // We want to get the parent info from lessonListToShow.
  // Example: if we want to add a "project" (lessonListToShow) into a
  // "course" (interestLesson), we need to get the parent of the "project".
  const interestLesson = lessonInformation(lessonListToShow).parent;
  // Definition of the lesson that needs to be assigned
  const lessonTobeAssigned = lessonListToShow;

  /**
   * Function to get all the data of the list
   * @returns data: Program | Course | Project | Module
   */
  const getData = async () => {
    // First, start the animation
    setIsLoadingData(true);

    // Get the data from the database record
    const response = await getCollectionRequest(`/api/${interestLesson}?includePublic=false`);

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Stop the animation
    setIsLoadingData(false);

    return response.content;
  };

  /**
   * Definition of the Add or remove action
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const selectAction = async (
    lesson: Program | Course | Project | Module,
    action: "add" | "remove"
  ) => {
    // Set the type of action
    setAddOrRemovePopUp(action);

    // And add or remove the data depending on the case
    let response = { successful: false, message: "Action not executed." };
    if (action === "add") {
      // Add lesson to be assigned to the current lesson (in this case the
      // parameters are inverted because we are assigning a child to a parent)
      response = await updateRecordRequest(
        {
          currentLessonType: interestLesson,
          currentLessonId: lesson._id,
          lessonsListType: lessonTobeAssigned,
          lessonsListIdsToAdd: [selectedDataId],
        },
        "/api/lessons/add-double-dependency"
      );
    } else if (action === "remove") {
      response = await updateRecordRequest(
        {
          currentLessonType: interestLesson,
          currentLessonId: lesson._id,
          lessonsListType: lessonTobeAssigned,
          lessonsListIdsToRemove: [selectedDataId],
        },
        "/api/lessons/add-double-dependency"
      );
    }

    // Check if the response was successful
    if (!response.successful) {
      alert(response.message);
      return false;
    }

    // Send the signal to show the pop up message
    setSuccessPopUpText(lesson.title);
    setShowSuccessPopUp(true);

    return true;
  };

  /**
   * Select create new inside the pop up
   */
  const handleCreateNew = () => {
    // If we are creating then, define the lesson to be added as child
    setLinkedId(selectedDataId);
    setLinkedLesson(lessonTobeAssigned);

    // Navigate to create a new lesson
    onCreateLesson(
      interestLesson as "programs" | "courses" | "projects" | "modules"
    );

    // And close the pop up
    setShowPopUp(false);
  };

  /**
   * Load lesson data when init
   */
  useEffect(() => {
    // Get all the data to show as options
    getData().then((data: Program[] | Course[] | Project[] | Module[]) => {
      // Set the data
      setLessonData(data);
    });
  }, []);

  /**
   * Timer for the success pop up message
   */
  useEffect(() => {
    // Starting the timer
    const timer = setTimeout(() => {
      // Once finished, close the pop up
      setShowSuccessPopUp(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [showSuccessPopUp]);

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={() => setShowPopUp(false)}
      />
      <motion.div
        className="fixed w-[462px] h-fit left-0 right-0 ml-auto mr-auto px-6 pt-6 pb-8 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
      >
        <div className="self-stretch justify-between items-center inline-flex">
          <div className="justify-start items-center gap-3 flex">
            <BackButtonHeaderPopUp onClick={() => setShowPopUp(false)} />
            <div className="text-neutral-600 text-lg font-normal font-sans leading-snug">
              Assign to {interestLesson.slice(0, -1)}
            </div>
          </div>
          <div
            className="group rounded justify-center items-center gap-2 flex cursor-pointer"
            onClick={handleCreateNew}
          >
            <MdAddCircleOutline className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
            <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
              Create new
            </div>
          </div>
        </div>
        <div className="max-h-[400px] grow shrink justify-start items-start gap-[5px] inline-flex overflow-y-auto pop-up-scrollbar">
          <div className="w-[403px] h-fit self-stretch flex-col justify-start items-start gap-4 inline-flex">
            {lessonData.length > 0 ? (
              lessonData.map((item: any, index: number) => {
                // Define the selected value
                let selectedInit = false;

                // Get the list of ids
                const children = item[lessonTobeAssigned];

                // Define the first state based on the _id
                if (children && children.length > 0) {
                  // Format in case of populated children fields
                  const childrenIds = children.map((item: any) => {
                    if (typeof item === "object") return item._id;
                    else return item;
                  });

                  // Modify the selected init based on this checking
                  selectedInit = childrenIds.includes(selectedDataId);
                }

                return (
                  <AssignLessonCard
                    key={index}
                    selectedLesson={lessonListToShow}
                    lesson={item}
                    numElements={
                      item[lessonTobeAssigned]
                        ? item[lessonTobeAssigned].length
                        : 0
                    }
                    selectedInit={selectedInit}
                    selectAction={selectAction}
                  />
                );
              })
            ) : !isLoadingData ? (
              <EmptyViewPopUp />
            ) : (
              <div className="w-full h-[400px] flex justify-center items-center">
                <LoadingDots bgColor="bg-neutral-300" />
              </div>
            )}
          </div>
        </div>
        <SuccessPopUpMessage
          lessonToAdd={interestLesson.slice(0, -1)}
          showSuccessPopUp={showSuccessPopUp}
          successPopUpText={successPopUpText}
          addOrRemove={addOrRemovePopUp}
          setShowSuccessPopUp={setShowSuccessPopUp}
        />
      </motion.div>
    </>
  );
};

export default AssignLessonPanel;
